import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { resolveProfilePhoto } from '../../utils/profilePhoto';
import { useAuth } from '../../context/AuthContext';
import AdminTalentBookingLinkCard from '../../components/AdminTalentBookingLinkCard';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import TalentJobHistory from '../../components/admin/TalentJobHistory';
import CriticalPayCard from '../../components/admin/CriticalPayCard';

import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, User, Mail, Phone, MapPin, Calendar, Star,
  Shield, Briefcase, Crown, Loader2, CheckCircle2, XCircle, Clock,
  KeyRound, Copy, ExternalLink, MessageSquare, Send
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription} from '../../components/ui/dialog';
import { toast } from 'sonner';
import LegacyDataCard from '../../components/LegacyDataCard';
import LegacyClientRelationshipsCard from '../../components/LegacyClientRelationshipsCard';
import { useInviteTalentToShift } from '../../components/InviteTalentToShiftProvider';
import AddToInviteCartButton from '../../components/AddToInviteCartButton';
import TalentMarketsCard from '../../components/TalentMarketsCard';
import MarketBadge from '../../components/MarketBadge';
import CertificationList from '../../components/CertificationList';
import { formatDate } from '../../utils/timeFormat';
import CertificationBadges from '../../components/CertificationBadges';  // Iter-225a
import CommunicationLogTab from '../../components/CommunicationLogTab';  // Iter-226e
import TalentAvailabilityCard from '../../components/TalentAvailabilityCard';  // Iter-226g
import AdminIdentityEditor from '../../components/admin/AdminIdentityEditor';  // Iter-281b Part 3
import CoCViolationsPanel from '../../components/admin/CoCViolationsPanel';

const AdminUserDetail = () => {
  const navigate = useNavigate();
  const { userId, role } = useParams();
  const { token, loading: authLoading } = useAuth();
  const { open: openInviteToShift } = useInviteTalentToShift();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  // Contact-client dialogs
  const [emailOpen, setEmailOpen] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [includeSms, setIncludeSms] = useState(true);
  const [sending, setSending] = useState(false);

  // Iter-224c — Portfolio photos rows (with id) so admin can promote
  // any of them to be the profile photo without re-uploading.
  const [portfolioRows, setPortfolioRows] = useState([]);
  const [setPhotoBusy, setSetPhotoBusy] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'talent') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/admin/users/${user.id}/portfolio-photos`);
        if (!cancelled) setPortfolioRows(res.data || []);
      } catch (_e) {
        if (!cancelled) setPortfolioRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const handleAdminSetProfile = async (photoId) => {
    setSetPhotoBusy(photoId);
    try {
      await api.post(`/admin/users/${user.id}/set-profile-photo`, {
        portfolio_photo_id: photoId,
        note: 'Promoted from portfolio via admin UI.',
      });
      // Reload user so the avatar reflects.
      await fetchUser();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e.response?.data?.detail || 'Could not set profile photo');
    } finally { setSetPhotoBusy(null); }
  };

  const fetchAbortRef = useRef(null);

  useEffect(() => {
    // Wait for auth context to finish hydrating. Without this gate, React
    // StrictMode's double-mount + interceptor token re-verification can race
    // and surface a spurious "Failed to load user" toast on navigation.
    if (authLoading) return;
    if (!token) return; // Interceptor will redirect if session is truly dead.

    // Cancel any in-flight request from the previous mount (StrictMode).
    if (fetchAbortRef.current) fetchAbortRef.current.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    fetchUser(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, authLoading, token]);

  // Client billing state
  const [billing, setBilling] = useState(null);
  const [billingSaving, setBillingSaving] = useState(false);
  const [billingForm, setBillingForm] = useState({
    client_type: 'marketplace',
    billing_model: 'pay_per_post',
    payment_terms: 'due_on_receipt',
    payment_terms_approved: true,
  });

  // Iter-191.1 — markets for the summary-header badge
  const [headerMarkets, setHeaderMarkets] = useState([]);
  useEffect(() => {
    if (!user || user.role !== 'talent') return;
    api.get(`/admin/users/${userId}/markets`)
      .then(r => setHeaderMarkets(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, [user, userId]);

  const fetchBilling = async () => {
    try {
      const res = await api.get(`/admin/users/${userId}/billing`);
      setBilling(res.data);
      setBillingForm({
        client_type: res.data.client_type || 'marketplace',
        billing_model: res.data.billing_model || 'pay_per_post',
        payment_terms: res.data.payment_terms || 'due_on_receipt',
        payment_terms_approved: !!res.data.payment_terms_approved,
      });
    } catch {}
  };

  useEffect(() => {
    if (user && (user.role === 'client' || user.role === 'agency')) fetchBilling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const saveBilling = async () => {
    setBillingSaving(true);
    try {
      // When setting net_15 / net_30 without explicit approval, warn
      const needsApproval = ['net_15', 'net_30'].includes(billingForm.payment_terms) && !billingForm.payment_terms_approved;
      if (needsApproval) {
        if (!window.confirm('Net terms require explicit approval. Approve now?')) {
          setBillingSaving(false);
          return;
        }
      }
      await api.patch(`/admin/users/${userId}/billing`, billingForm);
      toast.success('Billing updated');
      fetchBilling();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update billing');
    } finally {
      setBillingSaving(false);
    }
  };

  const fetchUser = async (signal, attempt = 0) => {
    try {
      setNotFound(false);
      const res = await api.get(`/admin/users/${userId}`, { signal });
      setUser(res.data);

      // Fetch role-specific profile (non-critical — swallow errors silently).
      if (res.data.role === 'talent') {
        try {
          const pRes = await api.get(`/discover/talent/${userId}`, { signal });
          setProfile(pRes.data);
        } catch {}
      }
    } catch (e) {
      // Aborted (StrictMode / navigation away) → stay quiet.
      const isCanceled = e.name === 'CanceledError' || e.code === 'ERR_CANCELED' || e.name === 'AbortError' || (signal && signal.aborted);
      if (isCanceled) return;

      const status = e.response?.status;

      // Session endpoints handle 401 globally — don't toast here.
      if (status === 401) return;

      // 404 → show the "User not found" fallback UI, no noisy toast.
      if (status === 404) {
        setNotFound(true);
        return;
      }

      // Network blip / 5xx → retry once before showing an error.
      const isTransient = !status || status >= 500;
      if (isTransient && attempt === 0) {
        await new Promise(r => setTimeout(r, 600));
        return fetchUser(signal, attempt + 1);
      }

      // Only toast for real, persistent server/network errors.
      toast.error(e.response?.data?.detail || 'Failed to load user');
    } finally {
      if (!signal || !signal.aborted) setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  if (notFound || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <p className="text-gray-600">User not found</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/admin/users')}>Back to Users</Button>
        </div>
      </div>
    );
  }

  const generateResetLink = async () => {
    setResetting(true);
    setResetResult(null);
    try {
      const res = await api.post(`/auth/admin/generate-reset-link/${user.id}`);
      setResetResult(res.data);
      toast.success('Reset link generated — expires in 24 hours');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate reset link');
    } finally {
      setResetting(false);
    }
  };

  const copyLink = async () => {
    if (!resetResult?.reset_link) return;
    await navigator.clipboard.writeText(resetResult.reset_link);
    toast.success('Link copied to clipboard');
  };

  // ============================================================
  // CONTACT ACTIONS (Send Message / Send Email)
  // ============================================================
  const firstName = user?.first_name || user?.company_name || 'there';
  const PLATFORM_LINK = 'https://app.ievntur.com';
  const isTalent = (user?.role || '').toLowerCase() === 'talent';

  // Iter-133j (2026-04-30) — split onboarding copy by role.
  // Sending the CLIENT body to a TALENT account confused recipients
  // and stalled activation; admins got the wrong template by default
  // because the modal was role-blind.
  const defaultEmailSubject = isTalent
    ? `Your iEvntür account is ready — activate your profile`
    : `Your iEvntür account is ready — let's get you activated`;

  const defaultEmailBody = isTalent
    ?
`Hi ${firstName},

Your iEvntür account has been created.

To get started, click below and set your password:

${PLATFORM_LINK}

👉 Select "Forgot Password" and use this email address to create your login.

Once inside, you can:
• Complete your profile
• Upload your ID for verification
• Apply to gigs and get booked
• Track your schedule and payments

If you need help getting set up, just reply to this email.

Welcome to iEvntür,
The iEvntür Team`
    :
`Hi ${firstName},

Your iEvntür account has been created and is ready to use. Log in here:
${PLATFORM_LINK}

From the portal you can:
• Post a shift or direct-booking role
• Review applicants and book talent
• Manage payouts, timesheets, and messages

If you need a hand with onboarding or want a walkthrough, just reply to this email and we'll jump on a quick call.

Welcome aboard,
The iEvntür Team`;

  const defaultMessageBody = isTalent
    ? `Hi ${firstName} — your iEvntür account is ready. Sign in at ${PLATFORM_LINK} (use Forgot Password with this email) to set your password, complete your profile, and start applying to gigs.`
    : `Hi ${firstName} — welcome to iEvntür! Your account is activated and ready to use. Sign in at ${PLATFORM_LINK} to post your first shift. Let us know if you'd like a walkthrough.`;

  const openEmailDialog = () => {
    setEmailSubject(defaultEmailSubject);
    setEmailBody(defaultEmailBody);
    setEmailOpen(true);
  };

  const openMessageDialog = () => {
    setMessageBody(defaultMessageBody);
    setMessageOpen(true);
  };

  const sendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    setSending(true);
    try {
      const res = await api.post(`/admin/users/${userId}/send-email`, {
        subject: emailSubject,
        body: emailBody,
        send_sms: includeSms,
      });
      const smsStatus = res.data?.sms?.status;
      if (smsStatus === 'sent' || smsStatus === 'sandbox') {
        toast.success(`Email + SMS sent to ${user.email}`);
      } else if (includeSms && smsStatus === 'skipped') {
        const reason = res.data?.sms?.reason || 'no phone';
        toast.success(`Email sent to ${user.email} (SMS skipped: ${reason})`);
      } else {
        toast.success(`Email sent to ${user.email}`);
      }
      setEmailOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const sendMessage = async () => {
    if (!messageBody.trim()) {
      toast.error('Message is required');
      return;
    }
    setSending(true);
    try {
      const res = await api.post(`/admin/users/${userId}/send-message`, {
        content: messageBody,
        send_sms: includeSms,
      });
      const smsStatus = res.data?.sms?.status;
      if (smsStatus === 'sent' || smsStatus === 'sandbox') {
        toast.success('Message + SMS sent');
      } else if (includeSms && smsStatus === 'skipped') {
        const reason = res.data?.sms?.reason || 'no phone';
        toast.success(`Message sent (SMS skipped: ${reason})`);
      } else {
        toast.success('Message sent');
      }
      setMessageOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const statusColor = user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-user-detail">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/users')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <button onClick={() => navigate('/admin/home')} className="text-sm font-bold text-[#03DFFC] hover:opacity-80 hidden sm:inline">iEvntür</button>
          <h1 className="text-lg font-semibold text-gray-900">User Detail</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {isTalent && <TalentJobHistory talentId={userId} />}
        {isTalent && <CriticalPayCard talentId={userId} />}
        {/* User Info Card */}
        <Card data-testid="user-info-card">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-full overflow-hidden bg-gradient-to-br from-cyan-100 to-cyan-50 flex items-center justify-center flex-shrink-0 cursor-default select-none" data-testid="user-detail-avatar" aria-label="Profile photo (informational only)" title="">
                {(() => {
                  const photo = resolveProfilePhoto(user);
                  return photo ? (
                    <img
                      src={photo}
                      alt={`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
                      className="w-full h-full object-cover pointer-events-none"
                      draggable={false}
                      onError={(e) => {
                        // Graceful fallback — if the URL is broken/expired we
                        // don't want a busted-image icon; drop to the cyan User glyph.
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null;
                })()}
                <User
                  className="w-8 h-8 text-cyan-400"
                  style={{ display: resolveProfilePhoto(user) ? 'none' : 'flex' }}
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h2 className="text-xl font-bold text-gray-900">{user.first_name || ''} {user.last_name || ''}</h2>
                  <Badge className={statusColor}>{user.is_active ? 'Active' : 'Inactive'}</Badge>
                  <Badge className="bg-gray-100 text-gray-600">{user.role}</Badge>
                  {user.is_premium && <Badge className="bg-amber-100 text-amber-700"><Crown className="w-3 h-3 mr-0.5" />Premium</Badge>}
                </div>
                <div className="space-y-1 text-sm text-gray-600">
                  <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-gray-400" />{user.email}</div>
                  {user.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-gray-400" />{user.phone}</div>}
                  {(user.city || user.state) && <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-400" />{[user.city, user.state].filter(Boolean).join(', ')}</div>}
                  {user.role === 'talent' && headerMarkets.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap pt-0.5" data-testid="userdetail-header-markets">
                      <MarketBadge markets={headerMarkets} size="xs" testId="userdetail-market-badge" />
                    </div>
                  )}
                  {user.created_at && <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-gray-400" />Joined {formatDate(user.created_at)}</div>}
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  {isTalent && (
                    <Button
                      size="sm"
                      onClick={() => navigate(`/admin/messages/dm/${user.id}`)}
                      className="bg-cyan-600 hover:bg-cyan-700 text-white"
                      data-testid="dm-message-btn"
                    >
                      <MessageSquare className="w-4 h-4 mr-1" /> Message
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openMessageDialog}
                    className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                    data-testid="send-message-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-1" /> Send Message
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openEmailDialog}
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    data-testid="send-email-btn"
                  >
                    <Mail className="w-4 h-4 mr-1" /> {isTalent ? 'Send Activation Email' : 'Send Email'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setResetOpen(true)}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="generate-reset-link-btn"
                  >
                    <KeyRound className="w-4 h-4 mr-1" /> Generate Reset Link
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Iter-281b Part 3 — Admin Identity Editor (talent only).
            Surgical edit of first/middle/last/nickname/ethnicity. NO autosave,
            NO GPS-consent side effects, NO draft-restore involvement. */}
        {user.role === 'talent' && (
          <AdminIdentityEditor
            userId={user.id}
            initial={{
              first_name:  user.first_name,
              middle_name: profile?.middle_name ?? user.middle_name,
              last_name:   user.last_name,
              nickname:    profile?.nickname    ?? user.nickname,
              ethnicity:   profile?.ethnicity   ?? user.ethnicity,
            }}
            onSaved={(updated) => {
              setUser((prev) => prev ? { ...prev, ...updated } : prev);
              setProfile((prev) => prev ? { ...prev, ...updated } : prev);
            }}
          />
        )}

        {user.role === 'talent' && (
          <CoCViolationsPanel talentId={userId} />
        )}

        {/* Iter-279 — Quick drill-down stat tiles. Each tile is tappable
            and links to the corresponding admin list filtered by this user.
            Counts are best-effort (loaded inline from the user object
            where available, blank otherwise). */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="user-stat-tiles">
          {user.role === 'talent' && (
            <>
              <button
                type="button"
                onClick={() => navigate(`/admin/bookings?talent=${userId}`)}
                className="bg-white border border-slate-200 hover:border-cyan-400 hover:shadow-md rounded-xl p-3 text-left transition"
                data-testid="stat-tile-bookings"
                aria-label="View this talent's bookings"
              >
                <p className="text-[11px] uppercase text-slate-500 font-semibold tracking-wide">Bookings</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5">{user.bookings_count ?? '—'}</p>
                <p className="text-[11px] text-cyan-600 mt-1">View all →</p>
              </button>
              <button
                type="button"
                onClick={() => navigate(`/admin/ratings?user=${userId}`)}
                className="bg-white border border-slate-200 hover:border-cyan-400 hover:shadow-md rounded-xl p-3 text-left transition"
                data-testid="stat-tile-ratings"
                aria-label="View this talent's ratings"
              >
                <p className="text-[11px] uppercase text-slate-500 font-semibold tracking-wide">Ratings</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5">{user.ratings_count ?? user.rating_count ?? '—'}</p>
                <p className="text-[11px] text-cyan-600 mt-1">View all →</p>
              </button>
              <button
                type="button"
                onClick={() => navigate(`/admin/payouts?talent=${userId}`)}
                className="bg-white border border-slate-200 hover:border-cyan-400 hover:shadow-md rounded-xl p-3 text-left transition"
                data-testid="stat-tile-earnings"
                aria-label="View this talent's earnings"
              >
                <p className="text-[11px] uppercase text-slate-500 font-semibold tracking-wide">Earnings</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5">{user.total_earnings != null ? `$${Number(user.total_earnings).toFixed(0)}` : '—'}</p>
                <p className="text-[11px] text-cyan-600 mt-1">View all →</p>
              </button>
              <button
                type="button"
                onClick={() => document.getElementById('certifications-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-white border border-slate-200 hover:border-cyan-400 hover:shadow-md rounded-xl p-3 text-left transition"
                data-testid="stat-tile-certifications"
                aria-label="Jump to certifications section"
              >
                <p className="text-[11px] uppercase text-slate-500 font-semibold tracking-wide">Certifications</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5">{user.certifications_count ?? '—'}</p>
                <p className="text-[11px] text-cyan-600 mt-1">Jump to ↓</p>
              </button>
            </>
          )}
          {user.role === 'client' && (
            <>
              <button
                type="button"
                onClick={() => navigate(`/admin/bookings?client=${userId}`)}
                className="bg-white border border-slate-200 hover:border-cyan-400 hover:shadow-md rounded-xl p-3 text-left transition"
                data-testid="stat-tile-bookings"
              >
                <p className="text-[11px] uppercase text-slate-500 font-semibold tracking-wide">Bookings</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5">{user.bookings_count ?? '—'}</p>
                <p className="text-[11px] text-cyan-600 mt-1">View all →</p>
              </button>
              <button
                type="button"
                onClick={() => navigate(`/admin/invoices?client=${userId}`)}
                className="bg-white border border-slate-200 hover:border-cyan-400 hover:shadow-md rounded-xl p-3 text-left transition"
                data-testid="stat-tile-invoices"
              >
                <p className="text-[11px] uppercase text-slate-500 font-semibold tracking-wide">Invoices</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5">{user.invoices_count ?? '—'}</p>
                <p className="text-[11px] text-cyan-600 mt-1">View all →</p>
              </button>
              <button
                type="button"
                onClick={() => navigate(`/admin/ratings?user=${userId}`)}
                className="bg-white border border-slate-200 hover:border-cyan-400 hover:shadow-md rounded-xl p-3 text-left transition"
                data-testid="stat-tile-ratings"
              >
                <p className="text-[11px] uppercase text-slate-500 font-semibold tracking-wide">Ratings given</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5">{user.ratings_given_count ?? '—'}</p>
                <p className="text-[11px] text-cyan-600 mt-1">View all →</p>
              </button>
              <button
                type="button"
                onClick={() => navigate(`/admin/users?company=${userId}`)}
                className="bg-white border border-slate-200 hover:border-cyan-400 hover:shadow-md rounded-xl p-3 text-left transition"
                data-testid="stat-tile-team"
              >
                <p className="text-[11px] uppercase text-slate-500 font-semibold tracking-wide">Team</p>
                <p className="text-xl font-bold text-slate-900 mt-0.5">{user.team_count ?? '—'}</p>
                <p className="text-[11px] text-cyan-600 mt-1">View all →</p>
              </button>
            </>
          )}
        </div>
        {profile && user.role === 'talent' && (
          <>
            {/* Iter-223c — Portfolio gallery (matches client + public booking
                view so admins see exactly what a hiring client sees). */}
            {Array.isArray(user.portfolio_images) && user.portfolio_images.length > 0 && (
              <Card data-testid="admin-user-portfolio-card">
                <CardHeader className="py-3 px-5">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    Portfolio ({user.portfolio_images.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5">
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="admin-portfolio-grid">
                    {user.portfolio_images.map((img, i) => {
                      const url = typeof img === 'string' ? img : (img?.url || img?.image_url);
                      if (!url) return null;
                      // Iter-224c — Match this URL against the portfolio_photos
                      // rows so admins can promote it. Legacy JSONB-sourced
                      // URLs won't have a matching row and just render as a
                      // simple thumbnail (no promote button).
                      const row = portfolioRows.find((r) => r.photo_url === url);
                      return (
                        <div key={i} className="relative group">
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="aspect-square rounded-md overflow-hidden border bg-gray-100 hover:ring-2 hover:ring-cyan-300 transition block"
                            data-testid={`admin-portfolio-img-${i}`}
                            title="Open full size"
                          >
                            <img src={url} alt={`Portfolio ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                          </a>
                          {row && (
                            <button
                              type="button"
                              onClick={() => handleAdminSetProfile(row.id)}
                              disabled={setPhotoBusy === row.id}
                              className="absolute bottom-1 left-1 right-1 text-[10px] py-0.5 px-1 rounded bg-white/90 hover:bg-white border border-cyan-200 text-cyan-700 opacity-0 group-hover:opacity-100 transition-opacity"
                              data-testid={`admin-set-profile-photo-btn-${i}`}
                              title="Use as profile photo"
                            >
                              {setPhotoBusy === row.id ? 'Setting…' : 'Use as Profile'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader className="py-3 px-5">
                <CardTitle className="text-sm font-semibold">Performance</CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-5">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="text-center p-3 bg-cyan-50 rounded-lg">
                    <p className="text-2xl font-bold text-cyan-700">{profile.completed_gigs || 0}</p>
                    <p className="text-[11px] text-gray-500">Shifts Done</p>
                  </div>
                  <div className="text-center p-3 bg-emerald-50 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-700">{profile.reliability_percent || 0}%</p>
                    <p className="text-[11px] text-gray-500">Reliability</p>
                  </div>
                  <div className="text-center p-3 bg-amber-50 rounded-lg">
                    <p className="text-2xl font-bold text-amber-700">{profile.rating ? profile.rating.toFixed(1) : 'N/A'}</p>
                    <p className="text-[11px] text-gray-500">Rating</p>
                  </div>
                  <div className="text-center p-3 bg-violet-50 rounded-lg">
                    <p className="text-2xl font-bold text-violet-700">{profile.performance_score || 0}</p>
                    <p className="text-[11px] text-gray-500">Score</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Certifications — Iter-202: always render (with empty state)
                so admins immediately see compliance status for bartenders
                and other regulated roles. */}
            <Card data-testid="admin-user-certifications-card" id="certifications-section">
              <CardHeader className="py-3 px-5">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Shield className="w-4 h-4 text-emerald-600" />
                  Certifications
                </CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-5">
                <CertificationList
                  certifications={profile.certifications}
                  variant="full"
                  showDocLink
                  adminMode
                  onReviewed={() => fetchUser()}
                  testIdPrefix="admin-user-cert"
                  emptyMessage="No certifications uploaded yet."
                />
                {/* Iter-225a — Assessment-based certifications (training_certifications). */}
                {Array.isArray(profile.training_certifications) && profile.training_certifications.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-200" data-testid="admin-user-training-certs">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
                      Assessment-Based Certifications
                    </p>
                    <CertificationBadges
                      certifications={profile.training_certifications}
                      variant="detailed"
                      showScore
                      showDate
                      testIdPrefix="admin-user-training-cert"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Booking link — admin override + retired-slug history */}
            <AdminTalentBookingLinkCard userId={userId} />

            {/* Iter-191 — Market preferences */}
            <TalentMarketsCard
              variant="admin"
              talentUserId={userId}
              suggestCity={profile?.city || user.city || ''}
              suggestState={profile?.state || user.state || ''}
            />

            {/* Legacy experience (admin-only) */}
            <LegacyDataCard talentId={userId} />

            {/* Per-client legacy relationships — powers "Worked with you before" tag */}
            <LegacyClientRelationshipsCard talentId={userId} />
          </>
        )}

        {/* Billing settings — clients & agencies */}
        {(user.role === 'client' || user.role === 'agency') && (
          <Card data-testid="admin-user-billing-panel">
            <CardHeader className="py-3 px-5">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-emerald-500" /> Billing & Payment Terms
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-5 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="text-xs text-gray-600 space-y-1">
                  <span className="block font-medium">Client Type</span>
                  <select
                    value={billingForm.client_type}
                    onChange={(e) => setBillingForm({ ...billingForm, client_type: e.target.value })}
                    className="w-full rounded-md border border-gray-200 px-2 py-2 text-sm bg-white"
                    data-testid="billing-client-type"
                  >
                    <option value="marketplace">Marketplace (self-service)</option>
                    <option value="agency">Agency (full-service)</option>
                  </select>
                </label>
                <label className="text-xs text-gray-600 space-y-1">
                  <span className="block font-medium">Billing Model</span>
                  <select
                    value={billingForm.billing_model}
                    onChange={(e) => setBillingForm({ ...billingForm, billing_model: e.target.value })}
                    className="w-full rounded-md border border-gray-200 px-2 py-2 text-sm bg-white"
                    data-testid="billing-model"
                  >
                    <option value="pay_per_post">Pay per post</option>
                    <option value="subscription">Subscription</option>
                    <option value="invoiced">Invoiced</option>
                    <option value="deposit">Deposit-based</option>
                  </select>
                </label>
                <label className="text-xs text-gray-600 space-y-1">
                  <span className="block font-medium">Payment Terms</span>
                  <select
                    value={billingForm.payment_terms}
                    onChange={(e) => setBillingForm({ ...billingForm, payment_terms: e.target.value })}
                    className="w-full rounded-md border border-gray-200 px-2 py-2 text-sm bg-white"
                    data-testid="billing-payment-terms"
                  >
                    <option value="due_on_receipt">Due on Receipt (default)</option>
                    <option value="net_15">Net 15</option>
                    <option value="net_30">Net 30</option>
                  </select>
                </label>
                <label className="text-xs text-gray-700 flex items-end gap-2">
                  <input
                    type="checkbox"
                    checked={billingForm.payment_terms_approved}
                    onChange={(e) => setBillingForm({ ...billingForm, payment_terms_approved: e.target.checked })}
                    className="w-4 h-4 accent-cyan-500"
                    data-testid="billing-terms-approved"
                  />
                  <span>Payment terms approved</span>
                </label>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <p className="text-[11px] text-gray-500">
                  {billingForm.payment_terms === 'due_on_receipt'
                    ? 'Deposit rule applies — 50% upfront when booking ≥ 28 days out.'
                    : billingForm.payment_terms_approved
                      ? `${billingForm.payment_terms.replace('_', ' ')} — overrides deposit rule.`
                      : `${billingForm.payment_terms.replace('_', ' ')} — NOT approved; client will still be billed upfront.`}
                </p>
                <Button
                  onClick={saveBilling}
                  disabled={billingSaving}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  size="sm"
                  data-testid="save-billing-btn"
                >
                  {billingSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Save Billing
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Admin Actions */}
        <Card>
          <CardHeader className="py-3 px-5">
            <CardTitle className="text-sm font-semibold">Actions</CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5 flex flex-wrap gap-2">
            <Button variant="outline" size="sm" data-testid="impersonate-btn-user-detail" onClick={() => {
              const reason = (window.prompt(
                'Reason for impersonating this user (required, min 10 characters):',
                '',
              ) || '').trim();
              if (reason.length < 10) {
                toast.error('Impersonation cancelled — a reason of at least 10 characters is required.');
                return;
              }
              api.post(`/admin/impersonate/start`, {
                target_user_id: userId,
                reason,
              }).then(res => {
                if (res.data.token) {
                  // Iter-183 P0 — preserve admin session + write fresh user
                  // object so AuthContext re-hydrates as the impersonated user.
                  localStorage.setItem('original_token', localStorage.getItem('token'));
                  const originalUser = localStorage.getItem('user');
                  if (originalUser) localStorage.setItem('original_user', originalUser);
                  localStorage.setItem('token', res.data.token);
                  localStorage.setItem('impersonating', 'true');
                  localStorage.setItem('impersonated_user_email', res.data.user_email || '');
                  localStorage.setItem('impersonator_email', res.data.impersonator_email || '');
                  localStorage.setItem('impersonation_reason', reason);
                  localStorage.setItem('impersonation_started_at', res.data.started_at || '');
                  localStorage.setItem('user', JSON.stringify({
                    id: res.data.user_id,
                    email: res.data.user_email,
                    role: res.data.user_role || res.data.role,
                    admin_role: null,
                    admin_permissions: {},
                  }));
                  window.location.href = res.data.redirect_url || '/';
                }
              }).catch((err) => toast.error(err?.response?.data?.detail || 'Impersonation failed'));
            }}>
              <User className="w-4 h-4 mr-1.5" />Impersonate
            </Button>
            {isTalent && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-cyan-400 text-cyan-700 hover:bg-cyan-50"
                  onClick={() => openInviteToShift(
                    userId,
                    user.first_name ? `${user.first_name}${user.last_name ? ' ' + user.last_name : ''}` : user.email,
                  )}
                  data-testid="invite-to-gig-user-detail-btn"
                >
                  <Send className="w-4 h-4 mr-1.5" /> Invite to Gig
                </Button>
                <AddToInviteCartButton
                  talent={{
                    user_id: userId,
                    name: user.first_name
                      ? `${user.first_name}${user.last_name ? ' ' + user.last_name : ''}`
                      : user.email,
                    photo_url: profile?.profile_photo_url,
                    role: profile?.role_name || profile?.primary_role,
                    city: profile?.city,
                    state: profile?.state,
                  }}
                  variant="pill"
                  testIdSuffix="user-detail"
                />
              </>
            )}
            {user.is_active ? (
              <Button variant="outline" size="sm" className="text-red-600 border-red-200" onClick={() => {
                api.patch(`/admin/users/${userId}/suspend`).then(() => { toast.success('User suspended'); fetchUser(); }).catch(() => toast.error('Failed'));
              }}>Suspend</Button>
            ) : (
              <Button variant="outline" size="sm" className="text-green-600 border-green-200" onClick={() => {
                api.patch(`/admin/users/${userId}/activate`).then(() => { toast.success('User activated'); fetchUser(); }).catch(() => toast.error('Failed'));
              }}>Activate</Button>
            )}
          </CardContent>
        </Card>

        {/* Iter-226g — Talent availability (admin read-only) */}
        {user.role === 'talent' && <TalentAvailabilityCard talentId={userId} />}

        {/* Iter-226e — Communication / Email Log */}
        <CommunicationLogTab
          userId={userId}
          email={user.email}
          testIdScope="admin-user-comm-log"
        />
      </div>
      <Dialog open={resetOpen} onOpenChange={(v) => { setResetOpen(v); if (!v) setResetResult(null); }}>
        <DialogContent className="max-w-md" data-testid="reset-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-amber-600" /> Generate Password Reset Link
            </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Generate a one-time secure link the user can click to set a new password. Expires in 24h.
          </DialogDescription>
          </DialogHeader>
          {!resetResult ? (
            <>
              <p className="text-sm text-gray-600">
                Issues a fresh one-time reset link for <strong>{user.email}</strong>.
                Link expires in <strong>24 hours</strong>, is single-use, and any prior
                open reset tokens will be invalidated. <br/><br/>
                Use this only when the user can't receive the reset email. Share via SMS,
                Slack, or phone — never post publicly.
              </p>
              <div className="flex items-center gap-2 justify-end mt-4">
                <Button variant="outline" onClick={() => setResetOpen(false)} disabled={resetting}>Cancel</Button>
                <Button
                  onClick={generateResetLink}
                  disabled={resetting}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="generate-reset-link-confirm-btn"
                >
                  {resetting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1" />}
                  Generate Link
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-emerald-700 font-medium">✅ Reset link ready — valid until {new Date(resetResult.expires_at).toLocaleString()}.</p>
              <div className="bg-gray-50 border rounded p-2 text-xs break-all font-mono" data-testid="reset-link-value">
                {resetResult.reset_link}
              </div>
              <div className="flex items-center gap-2 justify-end mt-2">
                <Button variant="outline" onClick={copyLink} data-testid="reset-link-copy-btn">
                  <Copy className="w-4 h-4 mr-1" /> Copy
                </Button>
                <Button variant="outline" onClick={() => window.open(resetResult.reset_link, '_blank')}>
                  <ExternalLink className="w-4 h-4 mr-1" /> Open
                </Button>
                <Button onClick={() => setResetOpen(false)} className="bg-cyan-600 hover:bg-cyan-700">Done</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Send Email dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-lg" data-testid="send-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-emerald-600" /> {isTalent ? 'Send Activation Email' : 'Send Email'}
            </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            {isTalent
              ? 'Sends the talent activation template — recipient sets their password and completes their profile.'
              : 'Sends the client onboarding template — recipient logs in to post shifts and review applicants.'}
          </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div
              className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-[11px] font-bold uppercase ${
                isTalent ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-cyan-50 border-cyan-200 text-cyan-800'
              }`}
              data-testid="send-email-template-chip"
            >
              <Mail className="w-3 h-3" />
              {isTalent ? 'TALENT activation template' : 'CLIENT onboarding template'}
              <span className="ml-auto font-normal text-[10px] text-gray-500">auto-selected by user role</span>
            </div>
            <div className="text-xs text-gray-500">
              To: <span className="font-medium text-gray-700">{user.email}</span>
            </div>
            <label className="block text-xs font-medium text-gray-600">
              Subject
              <input
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="mt-1 w-full border border-gray-200 rounded-md px-3 py-2 text-sm"
                data-testid="send-email-subject"
              />
            </label>
            <label className="block text-xs font-medium text-gray-600">
              Message
              <textarea
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                rows={12}
                className="mt-1 w-full border border-gray-200 rounded-md px-3 py-2 text-sm font-mono"
                data-testid="send-email-body"
              />
            </label>
            <p className="text-[11px] text-gray-500">
              Sends via the platform email service. Includes the activation link <strong>app.ievntur.com</strong>.
            </p>
            <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none" data-testid="email-include-sms-label">
              <input
                type="checkbox"
                checked={includeSms}
                onChange={(e) => setIncludeSms(e.target.checked)}
                className="w-4 h-4 accent-cyan-500"
                data-testid="email-include-sms"
              />
              <span>Also send SMS to <span className="font-mono">{user.phone || '—'}</span> (30-min cooldown prevents duplicates)</span>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEmailOpen(false)} disabled={sending}>Cancel</Button>
              <Button
                onClick={sendEmail}
                disabled={sending}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="send-email-confirm-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                Send Email
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send In-App Message dialog */}
      <Dialog open={messageOpen} onOpenChange={setMessageOpen}>
        <DialogContent className="max-w-lg" data-testid="send-message-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-cyan-600" /> Send In-App Message
            </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Delivered to their in-app inbox and mobile push if they've opted in.
          </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-gray-500">
              To: <span className="font-medium text-gray-700">{user.first_name || user.company_name || user.email}</span>
            </div>
            <label className="block text-xs font-medium text-gray-600">
              Message
              <textarea
                value={messageBody}
                onChange={(e) => setMessageBody(e.target.value)}
                rows={8}
                className="mt-1 w-full border border-gray-200 rounded-md px-3 py-2 text-sm"
                data-testid="send-message-body"
              />
            </label>
            <p className="text-[11px] text-gray-500">
              Delivered to the user's in-app inbox and notification bell. Includes a direct link to <strong>app.ievntur.com</strong>.
            </p>
            <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none" data-testid="message-include-sms-label">
              <input
                type="checkbox"
                checked={includeSms}
                onChange={(e) => setIncludeSms(e.target.checked)}
                className="w-4 h-4 accent-cyan-500"
                data-testid="message-include-sms"
              />
              <span>Also send SMS to <span className="font-mono">{user.phone || '—'}</span> (30-min cooldown prevents duplicates)</span>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setMessageOpen(false)} disabled={sending}>Cancel</Button>
              <Button
                onClick={sendMessage}
                disabled={sending}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="send-message-confirm-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                Send Message
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUserDetail;
